/**
 * The demo, on a real store.
 *
 * Until now the deployed page read three arrays compiled into the bundle. It
 * could show a generated lens and nothing else: creating an object, adding a
 * property, editing a value and surviving a reload were all impossible, which
 * is five of the six rows in `Agents/MILESTONE.md`.
 *
 * Objects now live in IndexedDB, created through the store's own API — the same
 * path a user takes when they press "New object". There is no fixture of a
 * *result* here: no hand-written `ObjectShape`, no hand-written spec. The arrays
 * below are seed content, and everything above them is derived.
 *
 * **On what has to persist.** Property keys are UUIDs minted by
 * `defineProperty`, and a manifest ref is content-addressed over those keys — so
 * re-seeding on every load would mint new keys, produce a different manifest,
 * and address a different scope. The objects would still be in IndexedDB and the
 * page would never find them. The bootstrap (pool → manifest + keys) is
 * therefore kept in localStorage next to the store, and its absence is what
 * "first run" means.
 */

import {
  openIndexedDbStore, createMemoryStore, StoreEngine,
  announceOn, attachTabInvalidation,
  createMemoryBlobs, openIndexedDbBlobs, sourceStoreOverWorker,
  defineDocumentKeys, completeDocumentKeys, documentKeysComplete, putDocumentManifests,
  derivedRoleReadBoundary, documentBodyProvider, loadDocument, saveDocument,
  type BlobStore,
  type DocumentKeys, type DocumentManifests,
  type EditorAuthority, type JournalId, type ManifestRef, type ObjectId, type ObjectReader,
  type ObjectStore, type PropertyKeyId, type Scope, type Value,
  type BroadcastChannelLike, type TabInvalidation,
  type DerivedRoleReadBoundary,
} from '@morph/store';
import type { DocumentBlockKind, Role, RoleKind } from '@morph/spec';

const BOOTSTRAP_KEY = 'morph.demo.bootstrap.v2';
const DB_NAME = 'morph-demo';

/** Injected by `build.mjs`, which is the only thing that knows the worker's
 *  hashed filename. Declared so a typecheck without the bundler still passes,
 *  and so its absence is a value rather than a ReferenceError. */
declare const __MORPH_SOURCE_WORKER__: string | undefined;

/**
 * Put the source write behind a Worker, or don't, and carry on either way.
 *
 * Decision `0043`. What this buys, measured on a 64 MiB source in local
 * Chromium: **555-584ms of main-thread stall becomes 4-18ms**, against 0021's
 * 1,824ms budget. The hashing and the IndexedDB write still cost what they cost
 * — about 95ms and 145ms here, ten times that on the deployed machine — they
 * stop being spent where a person can feel them.
 *
 * ## Every failure degrades to the store that was already working
 *
 * There is no environment where this is required: `blobs` is a complete,
 * correct store before it is wrapped, and the wrapper only moves where one
 * method runs.
 *
 * **Two different failures, and this function only sees one of them.** @Codex,
 * P2 on #184: *"When the hashed worker asset returns 404 or its module fails
 * during evaluation, `new Worker()` normally succeeds and reports the failure
 * asynchronously, so this `catch` never selects `inner`."*
 *
 * Right, and the paragraph that used to be here said the opposite — it listed
 * *"a 404 on the worker URL"* among the cases that *"end the same way, the
 * unwrapped store"*. A constructor that does not throw cannot be caught, so
 * that sentence described a fallback this file does not have.
 *
 * ```
 * no `Worker`, no `define`, a throwing constructor   caught HERE
 * a 404, a bad MIME type, a module that throws       reported to `onerror`,
 *                                                    handled in the wrapper
 * ```
 *
 * The second class is handled where it is observable: `sourceStoreOverWorker`
 * waits for the worker to announce itself before transferring anything, so a
 * worker that never loads costs the first caller nothing, and every later write
 * goes to `inner`. Both classes end at the same place; only one of them ends
 * there because of this `catch`.
 *
 * **Silent on purpose, and this is the one that deserves an argument.** A
 * person cannot act on "the source store is on the main thread", and a console
 * error at boot that nobody can fix is noise that trains people to ignore the
 * console. What can act on it is the gate: `e2e/ingest-budget.mjs` measures the
 * stall, so a worker that stopped loading shows up as the ceiling clause going
 * red, which is the only place the difference is real. That is also why the
 * deployed drive pins the worker's hash separately from the bundle's — a 404
 * and a Worker that did not help produce the same number, and only the hash
 * tells them apart.
 *
 * A `worker.onerror` AFTER construction is different and is not silent — that
 * is `sourceStoreOverWorker`'s `SourceTransferLost`, because by then a person's
 * file is in flight and the bytes are gone with it.
 */
function sourceStoreBehindAWorker(inner: BlobStore): BlobStore {
  const url = typeof __MORPH_SOURCE_WORKER__ === 'string'
    ? __MORPH_SOURCE_WORKER__ : undefined;
  const Ctor = (globalThis as { Worker?: new (u: string, o?: { type: string }) => unknown }).Worker;
  if (!url || !Ctor) return inner;
  try {
    // `type: 'module'`, because the entry is built as ESM and imports
    // `@morph/store`. A classic worker would fail on the first `import` with a
    // syntax error inside the worker, which surfaces as onerror rather than as
    // a throw here — so this is a real behavioural choice, not boilerplate.
    return sourceStoreOverWorker(inner, new Ctor(url, { type: 'module' }) as never);
  } catch {
    return inner;
  }
}

import { POOL_DEFS, type Def, type PoolDef, type Targets } from './pools.js';

export type { Def, Targets };

export interface DemoPool extends PoolDef {
  /** Filled in during bootstrap. */
  manifest: ManifestRef;
  scope: Scope;
  keys: Record<string, PropertyKeyId>;
}

const P = 'https://image.tmdb.org/t/p/w342';

/** Directors, with enough of their own properties to be worth previewing. A
 *  relation target that carries only a name renders a preview with a name in
 *  it, which demonstrates nothing — the point of resolving a relation is that
 *  the thing behind it is a whole object. They reuse the film keys: a person's
 *  `date` is when they were born and their `origin` is where from. */
const DIRECTOR_ROWS: Readonly<Record<string, readonly [string, string]>> = {
  'René Clément': ['1913-03-18', 'France'],
  'Andrei Tarkovsky': ['1932-04-04', 'USSR'],
  'Wong Kar-wai': ['1958-07-17', 'Hong Kong'],
  'Jean-Pierre Melville': ['1917-10-20', 'France'],
  'Wim Wenders': ['1945-08-14', 'West Germany'],
  'Claire Denis': ['1946-04-21', 'France'],
  'Věra Chytilová': ['1929-02-02', 'Czechoslovakia'],
  'Akira Kurosawa': ['1910-03-23', 'Japan'],
  'Jonathan Glazer': ['1965-03-26', 'United Kingdom'],
  'Agnès Varda': ['1928-05-30', 'France'],
  'Elem Klimov': ['1933-07-09', 'USSR'],
};

const FILM_ROWS = [
  ['Plein Soleil', '1960-03-10', 'René Clément', 118, 4.5, 'France', `${P}/xGVRU34izQwG8YQNfVOSfKPhD3S.jpg`],
  ['Stalker', '1979-05-25', 'Andrei Tarkovsky', 162, 4.5, 'USSR', `${P}/1qhOyf5C4s9ZdvY8d5JDx9DFMeT.jpg`],
  ['Chungking Express', '1994-07-14', 'Wong Kar-wai', 102, null, 'Hong Kong', `${P}/43I9DcNoCzpyzK8JCkJYpHqHqGG.jpg`],
  ['Le Samouraï', '1967-10-25', 'Jean-Pierre Melville', 105, 4.5, null, `${P}/5Fa6o5nfUPEatQ9b3OwEvdEdR7T.jpg`],
  ['Paris, Texas', '1984-05-19', 'Wim Wenders', 145, 4.5, 'West Germany', `${P}/sP27Qm4THyRZyHjHYMfIDtJP6YE.jpg`],
  ['Beau Travail', '1999-09-01', 'Claire Denis', 92, null, 'France', `${P}/5ySa1aQaT7kMcAbKwlS1HXxp2hM.jpg`],
  ['Daisies', '1966-12-30', 'Věra Chytilová', 76, 4.0, null, `${P}/8sxMhdn3i1Pn8OlGCBBjr9rjP1y.jpg`],
  ['Ran', '1985-06-01', 'Akira Kurosawa', 162, 5.0, 'Japan', `${P}/dGgiXMWDcS5aJTBEFu7bmaQPNox.jpg`],
  ['Under the Skin', '2014-03-14', 'Jonathan Glazer', 108, null, 'United Kingdom', `${P}/55wmcXJIDYITr7JDijJTdvwSaAv.jpg`],
  ['Cléo from 5 to 7', '1962-04-11', 'Agnès Varda', 90, 4.5, null, `${P}/oelBStY4xpguaplRv15P3Za7Xsr.jpg`],
  ['Come and See', '1985-07-09', 'Elem Klimov', 142, 5.0, 'USSR', `${P}/qNbMsKVzigERgJUbwf8pKyZogpb.jpg`],
  ['In the Mood for Love', '2000-09-29', 'Wong Kar-wai', 98, null, 'Hong Kong', `${P}/iYypPT4bhqXfq1b6EnmxvRt6b2Y.jpg`],
] as const;

/**
 * A media-first pool of genuinely public-domain material.
 *
 * This exists because `ACCEPTANCE.md` asks for Fabric — "media-first collection
 * at scale, mixed content types, everything renderable: video, audio, PDF,
 * transcript" — and because until `av` existed the product could not express
 * any of it.
 *
 * Two decisions worth stating:
 *
 * **The data is honest.** Attaching a sample video to *Stalker* and calling it
 * a trailer would be a lie sitting in the object store, and this repo is
 * unusually strict about not lying in comments; data deserves the same rule.
 * These are real Prelinger and LibriVox items, public domain, at their real
 * URLs.
 *
 * **The rows are ragged on purpose, in a way the films pool cannot be.** Some
 * items are film with a poster and no audio; some are recordings with audio, no
 * video and no poster of their own. That is per-object property sets doing the
 * thing the product claims — one collection, genuinely different objects — and
 * `items[].when` is what will eventually pick a template per row.
 *
 * `preload="metadata"` in the renderer means a browser fetches headers and not
 * the file, so a 33 MB short costs a few kilobytes until someone presses play.
 */
const ARCHIVE_ROWS = [
  {
    source: 'Internet Archive',
    title: 'A Case of Spring Fever',
    date: '1940-01-01',
    author: 'Jam Handy Organization',
    video: 'https://archive.org/download/CaseofSp1940/CaseofSp1940_512kb.mp4',
    poster: 'https://archive.org/services/img/CaseofSp1940',
    transcript:
      'Gilbert Willoughby is punished for complaining about springs by Coily, an '
      + 'animated spring who subjects him to the hassles of a springless world.',
  },
  {
    source: 'Internet Archive',
    title: 'Spot News',
    date: '1937-01-01',
    author: 'Jam Handy Organization',
    video: 'https://archive.org/download/SpotNews1937/SpotNews1937_512kb.mp4',
    poster: 'https://archive.org/services/img/SpotNews1937',
    transcript:
      'A dramatization of how photographs are transmitted by wire, an exciting new '
      + 'technology in the 1930s.',
  },
  {
    // No video and no poster: a recording is not a film, and pretending it has
    // a frame would put a grey rectangle on screen where nothing belongs.
    source: 'Internet Archive',
    title: 'A Scandal in Bohemia',
    date: '1891-07-01',
    author: 'Arthur Conan Doyle',
    audio:
      'https://archive.org/download/adventures_sherlock_holmes_rg_librivox/'
      + 'adventuresholmes_01_doyle_64kb.mp3',
    transcript:
      'To Sherlock Holmes she is always the woman. I have seldom heard him mention '
      + 'her under any other name.',
  },
  {
    source: 'Internet Archive',
    title: 'The Red-Headed League',
    date: '1891-08-01',
    author: 'Arthur Conan Doyle',
    audio:
      'https://archive.org/download/adventures_sherlock_holmes_rg_librivox/'
      + 'adventuresholmes_02_doyle_64kb.mp3',
    transcript:
      'I had called upon my friend, Mr. Sherlock Holmes, one day in the autumn of '
      + 'last year, and found him in deep conversation with a very stout, '
      + 'florid-faced elderly gentleman with fiery red hair.',
  },
] as const;

/**
 * THE THREE NOTES THAT ARRIVE WITH STRUCTURE — task `0199` half 2, and the
 * last of Obsidian finding 6.
 *
 * `0199` half 1 taught the editor to MAKE a heading, a list and a quote, and
 * they survive a reload because they are stored as blocks. What it left is
 * that the vault looks like a vault only after somebody types: eight notes of
 * one flat paragraph each, which is not what a person meets when they open
 * Obsidian.
 *
 * These are CANONICAL DOCUMENT CONTENT, written here as blocks rather than as
 * prose with markers in it. `migrateBodyToBlocks` deliberately produces
 * paragraphs and nothing else — its docstring calls the reverse "byte-for-byte
 * lossless without smuggling markup into canonical content" — so a seeded `# `
 * would have arrived as the literal characters, which is `0025`'s whole point
 * about authored meaning.
 *
 * FOUR NOW, and the fourth is the one the Obsidian door OPENS ON — @Codex's
 * deployed viewed verdict on `f6da07c`, which named this the single thing most
 * wrong with that row: *"the initial document is still dramatically too sparse
 * and form-like compared with the canonical note"*, against a reference that
 * shows a title, a heading, prose, a real list and a substantial quote.
 *
 * This list said `THREE, and only ones nothing asserts` and excluded
 * `Fixed-point layout` for exactly that reason — so the notes with structure
 * were the ones nobody looks at, and the note every person meets first was the
 * bare one. That is the caution being right about the risk and wrong about the
 * product.
 *
 * It is safe for the reason the guard below already gives: the lead paragraph
 * of each is the row's own `body` string unchanged, prepended by `authored`,
 * so an excerpt, a search hit and a card's first line all still say what they
 * said. Everything added comes AFTER it.
 *
 * (The count that sentence carried was stale too. Re-derived rather than
 * trusted, with `grep -rl 'Fixed-point layout' --include=*.mjs --include=*.ts`
 * minus this file: twelve files name it — eleven tests and harnesses, plus the
 * renderer's own source. The sentence said four.)
 *
 * The body a lens reads is DERIVED from this once it is stored —
 * `documentBodyProvider` is already wired into `effective` — so nothing here
 * is a second copy of the prose that could drift from it.
 */
const NOTE_DOCUMENTS: Record<string, readonly { kind: 'paragraph' | 'heading' | 'quote' | 'list-item'; text: string; level?: 2 }[]> = {
  'Taste as a fold': [
    { kind: 'heading', level: 2, text: 'Why a fold' },
    { kind: 'paragraph', text: 'Every correction is an entry. The model is what you get by replaying them, so nothing is lost and nothing is authoritative but the ledger.' },
    { kind: 'list-item', text: 'Reversible: drop the last entry and the model is what it was.' },
    { kind: 'list-item', text: 'Inspectable: every preference points at the gesture that taught it.' },
    { kind: 'list-item', text: 'Cheap: the fold is a reduce, not a training run.' },
    { kind: 'quote', text: 'A preference you cannot explain is a preference you cannot withdraw.' },
  ],
  /* TRUE OF THE SUBJECT, not filler — @Codex: *"arbitrarily tightening padding
     or adding filler would only disguise the missing composition."* Every line
     here is `0016`'s own content: the cycle this note's body states, and the
     four properties that task requires a fixed-point pass to PROVE. */
  'Fixed-point layout': [
    { kind: 'heading', level: 2, text: 'Four properties, proved rather than asserted' },
    { kind: 'paragraph', text: 'A single pass resolves the cycle by getting it wrong in a fixed direction. Measure, constrain, re-measure is the shape; what makes it safe is what the pass has to prove.' },
    { kind: 'list-item', text: 'Termination: a bounded iteration count, with the bound justified rather than picked, and what happens at the bound stated as a product decision.' },
    { kind: 'list-item', text: 'An oscillation guard: two layouts that swap between each other forever fail closed to a stable, valid one \u2014 never to the last one tried.' },
    { kind: 'list-item', text: 'Determinism: the same spec in the same environment yields the same IR, compared across independent runs rather than against itself.' },
    { kind: 'list-item', text: 'Idempotence: feeding a converged IR back through the pass changes nothing.' },
    { kind: 'quote', text: 'Non-convergence reaches a person as flicker, and flicker is the fastest way for a generated interface to lose trust.' },
  ],
  'Closed core, open registry': [
    { kind: 'heading', level: 2, text: 'The two halves' },
    { kind: 'paragraph', text: 'The vocabulary is fixed so that every reader agrees on what a spec means. The registry is open so that a seventh app needs no new words.' },
    { kind: 'list-item', text: 'A module declares a contract and a version.' },
    { kind: 'list-item', text: 'A host that lacks one refuses in words, not in silence.' },
    { kind: 'quote', text: 'Modules are data too, which is why the two halves do not contradict each other.' },
  ],
  'Inline composition': [
    { kind: 'heading', level: 2, text: 'The undo stack is one stack' },
    { kind: 'paragraph', text: 'A nested editor shares the document\u2019s undo history, so an undo pressed outside can rewrite text inside.' },
    { kind: 'list-item', text: 'Never await between removing an island and re-inserting it.' },
    { kind: 'list-item', text: 'Restore the caret from the block id, never from a DOM offset.' },
  ],
};

/** The notes the seed writes, exported so a TEST can ask the producer what a
 *  seeded body says instead of copying the string. `notes-vault-upgrade`
 *  needs it to build the one state the document seeding heals — a note with
 *  the seed's own body and no canonical document — and a literal there would
 *  be this repo's *a count copied from a document* defect in a fixture. */
export const NOTE_ROWS = [
  ['Fixed-point layout', 'Measure, constrain, re-measure. The cycle is real: metadata budget depends on width, width depends on column count, column count depends on the budget.', 'Claude', '2026-08-14', 1240],
  ['Pool abstraction gate', 'Generalize on the way in, specialize on the way out. The genome carries slot roles and nothing else.', 'Codex', '2026-08-15', 860],
  ['Binding direction', 'A binding carries direction. A read binding that can mutate is a hole in the whole safety argument.', 'Claude', '2026-08-15', 610],
  ['Density is discrete', 'Three legible states, not a scalar. A float produces valid, unreadable layouts.', 'Codex', '2026-08-16', 430],
  ['Inline composition', 'Chromium keeps one document-wide undo stack, so undo from the outer editor rewrites text inside a nested module.', 'Codex', '2026-08-20', 1980],
  ['Taste as a fold', 'The model is a fold over an append-only ledger, which buys reversibility and inspectability at once.', 'Claude', '2026-08-17', 720],
  ['Closed core, open registry', 'The vocabulary is closed. The module registry is open. Those are compatible because modules are data too.', 'Claude', '2026-08-18', 1510],
  ['Query terminates', 'No unbounded traversal. A relation traversal needs a declared maximum depth, enforced before execution.', 'Codex', '2026-08-19', 540],
] as const;

/**
 * Which note builds on which — the vault half of the Obsidian row.
 *
 * These are real citations rather than filler: the layout note depends on
 * density being discrete because a scalar density has no budget to solve
 * against, and the pool gate depends on binding direction because a read
 * binding that can mutate is the hole the whole gate argument closes.
 *
 * Cycles are deliberate and correct. `Pool abstraction gate` and
 * `Binding direction` cite each other, because in a vault of ideas they do; a
 * citation graph is not a tree. Nothing here needs it to be acyclic — the notes
 * are all created first and linked afterwards, and `traverse` is bounded by a
 * declared depth rather than by hoping the data has no loops.
 *
 * `Density is discrete` cites nothing. Its ref slot is then ABSENT rather than
 * empty, which is the rule the films and archive pools already follow, and it
 * keeps the pool's `relation.peer` coverage honestly below 1.
 */
const NOTE_LINKS: Readonly<Record<string, readonly string[]>> = {
  'Fixed-point layout':      ['Density is discrete'],
  'Pool abstraction gate':   ['Binding direction', 'Closed core, open registry'],
  'Binding direction':       ['Pool abstraction gate'],
  'Inline composition':      ['Closed core, open registry'],
  'Taste as a fold':         ['Pool abstraction gate'],
  'Closed core, open registry': ['Query terminates', 'Pool abstraction gate'],
  // TWICE, deliberately: the first fills the ref slot and the second becomes an
  // edge, so this note holds TWO rendered occurrences of ONE object.
  //
  // @Codex: *"`NOTE_LINKS` gives `Pool abstraction gate` two distinct targets …
  // so the old positional traversal would return two edges in matching order
  // and pass. This uniqueness assertion actually rejects the required case
  // where a slot-backed reference and an edge point to the same object."*
  //
  // Right, and the shape task 0078 exists for was not in the demo at all — so
  // the defect it fixed had never been reachable in the product, and neither
  // was the fix. `traverse` dedupes edges by `from>to`, which is what made a
  // positional lookup open the following target.
  'Query terminates':        ['Binding direction', 'Binding direction'],
};

/**
 * Tasks, with a SPAN each — a start and an end, not just a last-updated stamp.
 *
 * Added for the timeline, and it is the fixture doing its job rather than the
 * fixture being bent to fit: a task genuinely has a day it was picked up and a
 * day it was finished, and every one of these dates was already implied by the
 * single `Updated` column that stood in for both. A pool whose objects have one
 * date can only ever draw moments, so a timeline built against it would have
 * looked like a working chart of dots and proved nothing about bars.
 *
 * Two rows deliberately carry NO end. An open task has not finished, and the
 * timeline draws those as moments — which is the case that would otherwise go
 * untested until somebody hit it in the product.
 */
/**
 * Tasks, with a STATUS and an AREA — two low-cardinality enums.
 *
 * The area is what makes a SUB-GROUPED board reachable. Notion's board and
 * Coda's both sub-group, `subGroupBy` has been in the schema and (since it
 * started drawing) in the renderer, and the generator now emits it — but
 * `pickSubGroupBy` needs a second groupable role and no pool had one. `origin`
 * and `status` are the only two enum roles in the vocabulary; films carried
 * `origin`, tasks carried `status`, and neither carried both.
 *
 * Which part of the system a task belongs to is a real field on a real task
 * board, not scaffolding to satisfy a test — the same standard @Codex applied
 * to this pool's third relation: *"this is a genuine task-manager capability …
 * it also exercises the exact product surface whose behaviour is already under
 * test."*
 *
 * Four values against four statuses, and deliberately not one area per status:
 * a board sub-grouped by a role that partitions the same way as its columns
 * draws one sub-section per column and proves nothing.
 */
const TASK_ROWS = [
  ['Land the object store', 'Done', 'Store', 'Codex', '2026-08-20', '2026-08-11', '2026-08-15'],
  ['Runtime edit layer', 'Done', 'Runtime', 'Codex', '2026-08-20', '2026-08-14', '2026-08-19'],
  ['Create objects and properties', 'In progress', 'Store', 'Claude', '2026-08-20', '2026-08-17', ''],
  ['Query sublanguage', 'Open', 'Store', 'Codex', '2026-08-20', '2026-08-12', '2026-08-18'],
  ['Fixed-point layout', 'Open', 'Generation', 'Codex', '2026-08-20', '2026-08-09', '2026-08-13'],
  ['Buy the design layer', 'In progress', 'Generation', 'Codex', '2026-08-19', '2026-08-15', '2026-08-21'],
  ['Scale to 100k objects', 'Open', 'Runtime', 'Codex', '2026-08-20', '2026-08-18', '2026-08-22'],
  ['Inline composition spike', 'In review', 'Runtime', 'Codex', '2026-08-20', '2026-08-16', '2026-08-20'],
  ['Adversarial pool pass', 'Open', 'Demo', 'Codex', '2026-08-20', '2026-08-19', ''],
  ['Derived colour system', 'Done', 'Generation', 'Claude', '2026-08-20', '2026-08-10', '2026-08-14'],
  ['Host protocol', 'Open', 'Runtime', 'Codex', '2026-08-20', '2026-08-13', '2026-08-17'],
  ['The sidebar is an object', 'Open', 'Demo', 'Codex', '2026-08-20', '2026-08-20', '2026-08-22'],
] as const;

/**
 * Which task is about which note — **the cross-doc reference**.
 *
 * Tasks and notes are two different documents. Different manifest, different
 * property keys, and until now nothing that crossed between them: a reference
 * from a task to a note projected to `{}`, because `projectOne` walks the
 * reading manifest's roles and a note carries none of the tasks manifest's
 * keys. That is the manifest doing its job, and task 0049 is the long version.
 *
 * So the tasks lens DECLARES the notes document, in `Scope.across`, and the
 * reference resolves through the manifest that addresses the target. Nothing
 * about the write knows it crosses a document — `s.set(task, note, {k:'ref'})`
 * is the same call the vault makes to cite a note. Only the reading lens
 * differs, and only because it said so.
 *
 * These are real: `Query sublanguage` is the work `Query terminates` argues
 * for, `Adversarial pool pass` is the pass `Pool abstraction gate` describes.
 * Five of twelve tasks carry one, which keeps the tasks pool ragged the same
 * way every other pool here is — a cross-doc reference that every row happened
 * to have would hide the case where a lens declares a document and an object
 * points at nothing in it.
 */
const TASK_NOTES: Readonly<Record<string, string>> = {
  'Fixed-point layout':       'Fixed-point layout',
  'Query sublanguage':        'Query terminates',
  'Runtime edit layer':       'Binding direction',
  'Inline composition spike': 'Inline composition',
  'Adversarial pool pass':    'Pool abstraction gate',
  'Buy the design layer':     'Taste as a fold',
};

/**
 * THE FIBERY WORKSPACE'S SEED — task 0150, phase 1.
 *
 * Ragged the way every pool here is: two features carry no sprint, two no
 * assignee, three no due date. Statuses are the ORDERED flow the future
 * process view will constrain against (Backlog → Building → Review →
 * Shipped); the order lives nowhere yet, which is honest — expressing it is
 * the design question 0150 sends to @Codex before code.
 *
 * The seed alone closes NOTHING — 0150's authoring clause requires a person
 * defining a type, adding a field and declaring a typed relation end-to-end.
 * This is the substrate that work happens on, not evidence of it.
 */
const FEATURE_ROWS = [
  // title, stage, effort, due, assignee, sprint
  ['Offline sync',      'Building', 8,  '2026-09-20', 'Ana',  'Sprint 2 — sync'],
  ['Dark mode',         'Shipped',  3,  '2026-08-25', 'Bo',   'Sprint 1 — polish'],
  ['CSV import',        'Review',   5,  '2026-09-10', 'Ana',  'Sprint 2 — sync'],
  ['Realtime cursors',  'Backlog',  13, '',           '',     ''],
  ['Mobile layout',     'Building', 8,  '2026-09-18', 'Cleo', 'Sprint 2 — sync'],
  ['Keyboard palette',  'Shipped',  2,  '2026-08-20', 'Bo',   'Sprint 1 — polish'],
  ['Share links',       'Review',   3,  '2026-09-08', 'Dara', 'Sprint 2 — sync'],
  ['Search index',      'Building', 8,  '',           'Cleo', 'Sprint 3 — reach'],
  ['Billing page',      'Backlog',  5,  '2026-10-01', '',     'Sprint 3 — reach'],
  ['Onboarding tour',   'Backlog',  3,  '',           'Dara', ''],
] as const;

const SPRINT_ROWS = [
  // title, goal, lead, start, end
  ['Sprint 1 — polish', 'Ship the small wins first.',      'Bo',   '2026-08-11', '2026-08-24'],
  ['Sprint 2 — sync',   'Offline is the bet.',             'Ana',  '2026-08-25', '2026-09-14'],
  ['Sprint 3 — reach',  'Find things, and be found.',      'Cleo', '2026-09-15', '2026-09-28'],
] as const;

/**
 * Feature ids by title, captured while the features pool seeds and read while
 * the sprints pool seeds — the two run in one bootstrap, in POOL_DEFS order.
 * On a returning session neither pool reseeds and this stays empty, which is
 * correct: the links already exist in the store. In the one degenerate state
 * where features are saved but sprints are not, sprints seed with their
 * feature slots ABSENT — declared, unseeded, the notes-`raised` precedent —
 * rather than guessing ids by title match against edited data.
 */
const featureIdsByTitle = new Map<string, ObjectId>();

/** What survives a reload, so the same objects are found again. */
interface Bootstrap {
  readonly pools: Record<string, { manifest: string; keys: Record<string, string> }>;
  /** The document vocabulary lives beside the pools rather than in its own
   *  storage key: it is defined once, in the same commit discipline, and a
   *  bootstrap that remembered one and forgot the other would define private
   *  keys again on every load and orphan every document written under the old
   *  ones. */
  readonly document: { keys: Record<string, string>; manifests: Record<string, string> };
}

const readBootstrap = (): Bootstrap | undefined => {
  try {
    const raw = localStorage.getItem(BOOTSTRAP_KEY);
    return raw ? (JSON.parse(raw) as Bootstrap) : undefined;
  } catch {
    return undefined;   // private mode, quota, corrupt entry — reseed is correct
  }
};

const writeBootstrap = (b: Bootstrap): boolean => {
  try {
    localStorage.setItem(BOOTSTRAP_KEY, JSON.stringify(b));
    return true;
  } catch {
    // Callers that have already changed the durable store need to know this
    // failed. Silently continuing would let the bootstrap point at the old
    // manifest and make the same migration mint fresh keys and edges on every
    // reload.
    return false;
  }
};

const text = (v: string): Value => ({ k: 'text', v });

export interface DemoStore {
  readonly store: ObjectStore;
  readonly editor: EditorAuthority;
  readonly pools: readonly DemoPool[];
  readonly seeded: boolean;
  readonly backend: ObjectStore['backend'];
  readonly document: { readonly keys: DocumentKeys; readonly manifests: DocumentManifests };
  /**
   * Where a person's own files live. Decision 0017.
   *
   * The same backend the graph got, for the same reason and by the same rule:
   * IndexedDB where it exists, memory where it does not, and memory outright on
   * the `?scale=N` measurement route, which must never touch a database
   * somebody cares about.
   */
  readonly blobs: BlobStore;
  /**
   * The persistent store `blobs` delegates to, when a Worker is in front of it.
   *
   * Absent on the memory routes, where there is nothing to substitute. It
   * exists for decision `0046`'s two direct-store substitutions and for
   * nothing else — the demo never reads it, and a write through it lands in
   * the same database as a write through `blobs`.
   */
  readonly direct?: BlobStore;
  /** Browser-tab invalidation is owned and closed by the browser host. */
  readonly tabs?: TabInvalidation;
  /**
   * The configured read boundary. Decision 0030: shape, identities and values
   * come from ONE coherent read with derived roles folded in, so a canonical
   * document keeps `body` in the public shape after its scalar is retired.
   *
   * Every consumer that wants shape AND values uses this. Calling
   * `store.deriveShape` and patching values afterwards is what 0030 rules out
   * by name.
   */
  readonly effective: DerivedRoleReadBoundary;
  /**
   * Re-evaluate one role inside the caller's coherent read. Render-time
   * provenance removes false affordances; this closes the race where another
   * tab migrates the object before the write snapshot is taken.
   */
  isDerivedRole(
    reader: ObjectReader, object: ObjectId, manifest: ManifestRef, role: Role,
  ): Promise<boolean>;
}

export interface DemoStoreOptions {
  /** Runs after an invalidation has reloaded the engine from persistence. */
  readonly onRefreshed?: (revision: number) => void;
}

/** How many synthetic objects `?scale=N` asked for, clamped and validated.
 *
 *  The value comes off the URL, so it is user input: a non-number, a negative,
 *  or `?scale=1e12` all resolve to "no". The ceiling is 250k because the store
 *  creates 100k in 1.38s and someone will eventually try a million to see what
 *  happens; a browser tab that never returns is a worse answer than a refusal.
 */
function scaleRequested(): number {
  try {
    const raw = new URLSearchParams(location.search).get('scale');
    if (raw === null) return 0;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(Math.floor(n), 250_000);
  } catch {
    // No `location` under a non-browser harness, which is a legitimate way to
    // run this and not a reason to fail.
    return 0;
  }
}

/** The measurement pool's id. Named rather than spelled twice, because the
 *  demo has to recognise it on boot to open on it — a string literal in two
 *  files is the shape that lets one of them quietly stop matching. */
export const SCALE_POOL_ID = 'scale';

/**
 * A synthetic pool of `n` objects, for measuring rather than for reading.
 *
 * Deliberately boring data: a title, a number and a low-cardinality enum. The
 * point is frame time at scale, and a pool that also happened to be
 * media-heavy would measure image decoding instead.
 */
async function seedScalePool(
  store: ObjectStore, editor: EditorAuthority, n: number,
): Promise<DemoPool> {
  const def = {
    id: SCALE_POOL_ID,
    label: `Scale (${n.toLocaleString()})`,
    typeHint: 'scale',
    note: `${n.toLocaleString()} synthetic objects, seeded from ?scale=${n} and never `
      + 'persisted. Here to measure p95 frame time, which ACCEPTANCE.md asks for '
      + 'and packages/store/test/scale.test.ts can only answer for the store.',
    defs: [
      { handle: 'title', label: 'Title', kind: 'text' as const, role: 'title' as const },
      { handle: 'quantity', label: 'Index', kind: 'number' as const, role: 'quantity' as const },
      { handle: 'status', label: 'Bucket', kind: 'enum' as const, role: 'status' as const },
    ],
  };

  const keys: Record<string, PropertyKeyId> = {};
  const committed = await store.edit(editor, (await store.snapshot()).revision, (s) => {
    for (const d of def.defs) {
      keys[d.handle] = s.defineProperty({ label: d.label, kind: d.kind, roleHint: d.role });
    }
    for (let i = 0; i < n; i++) {
      s.create({ typeHint: def.typeHint, properties: [
        [keys.title!, { k: 'text', v: `Object ${i}` }],
        [keys.quantity!, { k: 'number', v: i }],
        [keys.status!, { k: 'enum', v: ['Open', 'Doing', 'Done', 'Held'][i % 4]! }],
      ] });
    }
  });
  if (!committed.ok) throw new Error(`seeding scale pool refused: ${committed.code}`);

  const manifest = await store.putManifest(editor,
    def.defs.map((d) => ({ role: d.role, key: keys[d.handle]! })));
  const scope: Scope = { manifest, typeHint: def.typeHint };
  if (store instanceof StoreEngine) store.register(scope);
  return { ...def, manifest, keys, scope };
}

export async function openDemoStore(options: DemoStoreOptions = {}): Promise<DemoStore> {
  const scale = scaleRequested();
  let channel: BroadcastChannelLike | undefined;
  /**
   * Configured for pools that DECLARE `body`, so a pool of films — and the
   * `?scale=N` route — does no document traversal at all. `supports` is 0030's
   * composition-time gate: *"returning false must perform no object reads."*
   */
  const effective = (
    open: ObjectStore, document: DemoStore['document'], built: readonly DemoPool[],
  ): Pick<DemoStore, 'effective' | 'isDerivedRole'> => {
    // Read the pools at call time: extending a manifest mutates the owning
    // pool's ref, and a boot-time set would stop recognising canonical Bodies
    // immediately after an unrelated property was added.
    const supportsBody = (scope: Scope): boolean => built.some((candidate) =>
      candidate.manifest === scope.manifest
      && candidate.defs.some((def) => def.role === 'body'));
    const provider = documentBodyProvider(
      document.keys, document.manifests, supportsBody,
    );
    return {
      effective: derivedRoleReadBoundary(open, [provider]),
      async isDerivedRole(reader, object, manifest, role) {
        const scope: Scope = { manifest };
        if (provider.supports?.(scope) === false) return false;
        const stored = await reader.get(object, { manifest });
        if (!stored) return false;
        return (await provider.derive({ reader, object: stored, scope }))
          .some((value) => value.role === role);
      },
    };
  };

  // IndexedDB where it exists, memory where it does not, so the page still runs
  // under a headless harness or a locked-down browser rather than blanking.
  //
  // EXCEPT on the measurement route, which is memory-only and never touches the
  // database at all.
  //
  // The first version of this seeded the synthetic pool through whatever store
  // was open, and wrote the bootstrap beforehand so the manifest would not be
  // remembered — then claimed in a comment, a task file and a commit message
  // that nothing persisted. That was false. Skipping the manifest hides the
  // pool; it does not unwrite the rows. Up to 250,000 synthetic objects and
  // their property definitions were left orphaned in the user's IndexedDB, and
  // a later `?scale=N` would have created fresh keys under the same typeHint,
  // so scope membership picked up prior runs and old rows projected empty
  // against the new manifest. Found by @Codex.
  //
  // My own browser check "verified" this by asserting the pool pill was gone
  // after a reload — the visible symptom of the claim rather than the claim.
  //
  // Refusing the database outright is the version that cannot be wrong: there
  // is no cleanup path to forget, and no way for a measurement run to touch
  // anything a person cares about. It costs the ability to measure
  // IndexedDB-backed reads, which is not what this route is for — frame time is.
  let store: ObjectStore;
  let blobs: BlobStore;
  /** The persistent store the Worker wrapper delegates to, when there is one.
   *  Absent on the memory routes, where there is nothing to substitute. */
  let direct: BlobStore | undefined;
  if (scale > 0) {
    store = createMemoryStore();
    blobs = createMemoryBlobs();
  } else {
    /**
     * The channel is opened in its OWN try, and that is not tidiness.
     *
     * @Codex, P1 on #166: *"If a browser exposes `BroadcastChannel` but its
     * constructor throws — for example, in a restricted browsing context —
     * this shared `try` treats that ancillary notification failure as if
     * IndexedDB itself were unavailable and switches the entire graph to the
     * memory store. Edits then appear to work but vanish on reload, and the
     * normal bootstrap write can replace durable manifest pointers with refs
     * from the temporary memory store."*
     *
     * Correct, and the second half is the worse half: the bootstrap in
     * localStorage would come to address manifests that only ever existed in
     * memory, so the NEXT load finds a durable database whose pointers name
     * nothing. Live invalidation is a convenience; the database is the product.
     */
    try {
      const BrowserChannel = typeof globalThis.document !== 'undefined'
        ? globalThis.BroadcastChannel : undefined;
      if (BrowserChannel) {
        channel = new BrowserChannel(`morph:store:${DB_NAME}`) as unknown as BroadcastChannelLike;
      }
    } catch {
      channel = undefined;                 // no cross-tab notice; still durable
    }
    try {
      store = await openIndexedDbStore({
        dbName: DB_NAME,
        ...(channel ? { onCommitted: announceOn(channel, DB_NAME) } : {}),
      });
      /**
       * The direct store is KEPT, beside the one behind the Worker.
       *
       * Decision `0046` required control 4: *"routing the ordinary ceiling
       * ingest through the real direct persistent store makes the
       * responsiveness clause red on the pinned deployed environment"*, and
       * control 3 asks the same of the doubled request — *"even if the content
       * is stored correctly."* Both need a handle to the implementation the
       * Worker replaced, and `0046` is explicit that the substitution must be
       * the REAL direct store rather than a synthetic freeze: *"it does not add
       * a synthetic busy-wait and does not wrap the operation with unrelated
       * blocking."*
       *
       * So it is the same object the wrapper delegates to — not a second
       * connection, which would measure IndexedDB contention rather than the
       * boundary. Nothing in the demo reads it; `main.ts` exposes it for a
       * harness exactly as it exposes `blobs`, and every write through it lands
       * in the same database.
       */
      direct = await openIndexedDbBlobs({ dbName: DB_NAME });
      blobs = sourceStoreBehindAWorker(direct);
    } catch {
      channel?.close();
      channel = undefined;
      store = createMemoryStore();
      blobs = createMemoryBlobs();
    }
  }

  const editor = await store.authorizeEditor({ id: 'demo' });
  if ('ok' in editor) throw new Error(`the editor door refused: ${editor.message}`);

  const prior = readBootstrap();
  /**
   * Defined ONCE and remembered, for the same reason the pool keys are.
   * `defineDocumentKeys` appends to the key registry every time it is called,
   * so a load that forgot the previous ids would write every later document
   * under private keys nothing else can read — the documents would still be
   * there and nothing would be able to find them.
   */
  /**
   * MIGRATED, NOT CAST — @Codex's P1 on `d881f5b`, and the defect was `0200`'s.
   *
   * This read `prior.document.keys` and asserted it into `DocumentKeys`. A
   * record persisted before `version` joined that interface therefore arrived
   * with `keys.version === undefined`, `persistDocument` called
   * `session.set(document, undefined, …)`, and the store refused the WHOLE
   * edit as `unknown-slot`: a returning installation could not edit a
   * canonical document at all.
   *
   * A cast is an assertion that a shape is what it says, and this one was
   * about a record written by an OLDER BUILD — the one kind of value a cast
   * cannot be trusted for. `completeDocumentKeys` defines whatever is absent
   * and names it, so the next key added to that table migrates through the
   * same line rather than through a second migration nobody writes.
   *
   * The manifests are NOT re-made: they exist and are durable, and re-making
   * them would mint new ones and orphan the documents that point at the old.
   * `added` is empty on every boot after the first, so the common path takes
   * no edit and no bootstrap write.
   */
  let documentMigrated = false;
  let document: { keys: DocumentKeys; manifests: DocumentManifests } | undefined;
  if (prior?.document) {
    let held = prior.document.keys as unknown as Partial<Record<keyof DocumentKeys, unknown>>;
    let keys: DocumentKeys | undefined;
    let added: readonly (keyof DocumentKeys)[] = [];
    if (documentKeysComplete(held)) {
      keys = held as unknown as DocumentKeys;
    } else {
      /**
       * A CONCURRENT WINNER IS NOT A BOOT FAILURE — @Codex's P2 on `2ef9cfb`.
       *
       * Two returning tabs load the same pre-version bootstrap, both snapshot
       * the same revision, and IndexedDB accepts one optimistic commit. The
       * other is refused `stale` — and the first version of this turned that
       * expected race into a thrown boot failure, so the second tab did not
       * start at all.
       *
       * Decision `0027` already says what `stale` means: the engine has
       * refreshed, plan it again. It is the reason `backend` is a separate
       * code — a caller told to retry against state that never arrived would
       * loop for ever — so retrying `stale` and only `stale` is the store's
       * own contract rather than a guess about it.
       *
       * ADOPT BEFORE RETRYING, because a retry alone is the wrong repair here:
       * `completeDocumentKeys` decides from the RECORD it is handed, not from
       * the store, so re-running it against the same stale `held` would mint a
       * second `version` key and stamp this tab's documents under an id the
       * winner's documents do not share. The winner wrote its record to
       * localStorage, so re-reading the bootstrap is what makes the retry
       * converge instead of fork. A partial winner — a build adding two keys
       * where this one adds one — advances `held` and the next attempt fills
       * only what is still missing.
       *
       * Bounded rather than a loop: three attempts, because each one either
       * adopts a complete record or writes against a fresh revision, and a
       * fourth would mean something other than a two-tab race.
       */
      const ATTEMPTS = 3;
      for (let attempt = 0; keys === undefined && attempt < ATTEMPTS; attempt += 1) {
        let filled: DocumentKeys | undefined;
        let filledAdded: readonly (keyof DocumentKeys)[] = [];
        const committed = await store.edit(editor, (await store.snapshot()).revision, (session) => {
          const out = completeDocumentKeys(session, held);
          filled = out.keys; filledAdded = out.added;
        });
        if (committed.ok && filled) {
          keys = filled; added = filledAdded;
          break;
        }
        if (committed.ok || committed.code !== 'stale' || attempt === ATTEMPTS - 1) {
          throw new Error(`migrating the document vocabulary was refused: ${JSON.stringify(committed)}`);
        }
        /* AND THIS READ HAS A WINDOW — @Codex's P2 on `48121a7`, filed as
           task `0226`. The winner commits to IndexedDB and only afterwards
           calls `writeBootstrap`, so a loser that gets here inside that gap
           reads the PRE-MIGRATION record, `held` names no version key,
           `completeDocumentKeys` appends one, and the retry succeeds: two
           keys, two stamping vocabularies, one pool.

           Not fixed here on purpose, and `0226` carries both candidates. The
           cheap one is a poll with a deadline, which is a timing heuristic in
           a boot path this repository has no way to drive — the same reason
           `0226` was open before this finding. The right one is a store that
           can ADOPT an existing key from its definition rather than always
           appending, which closes the race by construction and is a
           `@morph/store` contract change with its own decision. */
        const winner = readBootstrap()?.document?.keys as
          undefined | Partial<Record<keyof DocumentKeys, unknown>>;
        if (winner !== undefined && documentKeysComplete(winner)) {
          keys = winner as unknown as DocumentKeys;
          added = [];
          break;
        }
        if (winner !== undefined) held = winner;
      }
      if (!keys) {
        throw new Error('migrating the document vocabulary did not settle after '
          + `${ATTEMPTS} attempts against a moving store`);
      }
      documentMigrated = added.length > 0;
    }
    document = { keys, manifests: prior.document.manifests as unknown as DocumentManifests };
    /**
     * RECORDED IMMEDIATELY, because the two later `writeBootstrap` calls both
     * sit behind an upgrade or a seed. A steady-state load reaches neither, so
     * without this line the migration would run again on every boot — and
     * `defineProperty` APPENDS, so each boot would mint a different `version`
     * key and stamp that boot's documents under it.
     *
     * AND A FAILED WRITE DOES NOT UNDO, which is the opposite of what the pool
     * upgrades below do, so the reason is here rather than assumed. There, an
     * unrecorded upgrade means the next load redoes it; here, undoing puts
     * `keys.version` back to `undefined` and `persistDocument` then refuses
     * EVERY canonical document edit — the P1 this migration exists to end. A
     * repeated migration costs a spare property id per boot and reads the same
     * (an unstamped document is version 1 by the rule in `document.ts`); a
     * rollback costs the person their editor. Degrading beats failing, which
     * is the bar the notes upgrade below already sets.
     */
    if (documentMigrated) {
      writeBootstrap({
        document: document as unknown as Bootstrap['document'],
        pools: prior.pools ?? {},
      });
    }
  }
  if (!document) {
    let keys: DocumentKeys | undefined;
    const committed = await store.edit(editor,
      (await store.snapshot()).revision, (session) => { keys = defineDocumentKeys(session); });
    if (!committed.ok || !keys) throw new Error('defining the document vocabulary was refused');
    document = { keys, manifests: await putDocumentManifests(store, editor, keys) };
  }
  // Per OPEN, not per process: a returning session whose features pool takes
  // the saved path must see this empty, or a test calling openDemoStore twice
  // in one module retains the first open's ids and masks the recovery path —
  // which is exactly how the throw below hid from the regression test
  // (@Codex, P1 on afa632b).
  featureIdsByTitle.clear();
  const pools: DemoPool[] = [];
  let seeded = false;

  for (const def of POOL_DEFS) {
    const saved = prior?.pools[def.id];
    if (saved && await addresses(store, saved.manifest as ManifestRef, def.typeHint)) {
      const legacyPool: DemoPool = {
        ...def,
        manifest: saved.manifest as ManifestRef,
        keys: saved.keys as Record<string, PropertyKeyId>,
        scope: { manifest: saved.manifest as ManifestRef, typeHint: def.typeHint },
      };
      const upgraded = await upgradePool(store, editor, legacyPool, pools);

      if (upgraded.journal) {
        // The store commit and manifest are durable before localStorage is.
        // Persist this pool's new address immediately. If that fails, undo the
        // edit so the old bootstrap cannot cause fresh keys and edges on every
        // reload. The orphaned content-addressed manifest is harmless: no
        // bootstrap addresses it and its newly defined key has been undone.
        const nextBootstrap: Bootstrap = {
          document: document as unknown as Bootstrap['document'],
          pools: {
            ...(prior?.pools ?? {}),
            ...Object.fromEntries(pools.map((p) =>
              [p.id, { manifest: p.manifest, keys: p.keys }])),
            [def.id]: {
              manifest: upgraded.pool.manifest,
              keys: upgraded.pool.keys,
            },
          },
        };
        if (!writeBootstrap(nextBootstrap)) {
          const rolledBack = await store.undo(editor,
            (await store.snapshot()).revision, upgraded.journal);
          if (!rolledBack.ok) {
            throw new Error(
              `upgrading ${def.id} could not record its bootstrap or roll back: `
              + rolledBack.code,
            );
          }
          pools.push(legacyPool);
          continue;
        }
      }

      pools.push(upgraded.pool);
      continue;
    }
    const fresh = await seedPool(store, editor, def, pools);
    /**
     * Record THIS pool before the loop moves on — the upgrade branch above
     * has always done this, and the seed branch not doing it became a real
     * window the day two NEW pools joined an already-deployed product
     * (@Codex, P1 on b44ab0f): every existing installation seeds features
     * and sprints on its next load, their objects are durable the moment
     * seedPool returns, and a tab closed before the single end-of-loop
     * bootstrap write left them unrecorded — so the next load seeded them
     * AGAIN, accumulating duplicates per retry. Writing per pool shrinks
     * the window to one pool's own seeding; the recorded-but-unlinked
     * half-state that leaves (features saved, sprints not) is the exact
     * degradation `featureIdsByTitle` documents — sprints reseed with
     * their feature slots absent rather than guessing.
     *
     * And a write that FAILS undoes the seed — the same P1's second half
     * (@Codex on e673be9): quota-exceeded storage READS the old bootstrap
     * fine and refuses the new one, so ignoring this result seeded another
     * durable copy of the new pools on every reload, unboundedly. Undone,
     * the load opens on the pools it could record — the notes-upgrade
     * guard's own bar, *"degrading beats failing to start"* — and a later
     * healthy load seeds once. The map clears so no later pool wires refs
     * to objects that no longer exist.
     */
    const recorded = writeBootstrap({
      document: document as unknown as Bootstrap['document'],
      pools: {
        ...(prior?.pools ?? {}),
        ...Object.fromEntries([...pools, fresh.pool].map((p) =>
          [p.id, { manifest: p.manifest, keys: p.keys }])),
      },
    });
    if (!recorded && prior) {
      // Only on an UPGRADE. A fresh store with blocked storage has no
      // bootstrap either way; undoing there would open the app empty, and
      // the notes-upgrade guard's bar is that it opens on what it has. The
      // upgrade case is different because the OLD bootstrap survives every
      // reload and re-triggers this seed forever — that is the unbounded
      // duplication, and it is what the undo prevents.
      const rolledBack = await store.undo(editor,
        (await store.snapshot()).revision, fresh.journal);
      if (!rolledBack.ok) {
        throw new Error(
          `seeding ${def.id} could not record its bootstrap or roll back: `
          + rolledBack.code,
        );
      }
      featureIdsByTitle.clear();
      continue;
    }
    pools.push(fresh.pool);
    seeded = true;
  }

  /**
   * SECOND PASS: resolve `across` now that every pool exists.
   *
   * @Codex, P1: *"Notes is processed before Tasks, while `scopeFor` resolves
   * `across` only from the pools already in `done`; this therefore silently
   * drops `tasks` … A `Raised by` link can consequently be written but its task
   * target projects as an unreadable empty value without an occurrence token,
   * leaving the new cross-document relation unable to display, open, or
   * remove."*
   *
   * Mine, and it arrived with the second Notes relation an hour ago. Tasks
   * points BACKWARD at notes and worked; notes points FORWARD at tasks and did
   * not — so the defect was invisible for exactly as long as every cross-pool
   * reference happened to run downhill through the seeding order.
   *
   * The comment on `scopeFor` even describes the behaviour as intended: *"a
   * declared pool that is not (yet) built is dropped rather than thrown on."*
   * That is right for the single pass, where a forward reference cannot be
   * resolved yet. It is wrong as a final answer, and nothing here had ever
   * needed the difference.
   *
   * Rebuilt for EVERY pool rather than for the ones that look wrong: a scope
   * recomputed from the complete set is the same object for a pool whose
   * references all ran backward, and a rule with an exception is a rule with a
   * place for the next one to hide.
   */
  for (let i = 0; i < pools.length; i += 1) {
    const pool = pools[i]!;
    const scope = scopeFor(pool, pool.manifest, pools);
    pools[i] = { ...pool, scope };
    if (store instanceof StoreEngine) store.register(scope);
  }

  // A pool big enough to measure, on request only.
  //
  // `ACCEPTANCE.md` asks for p95 FRAME time at 100k objects, and
  // `packages/store/test/scale.test.ts` measures only the store in Node. @Codex
  // pointed out the gap in the handoff rather than in the code: there was no
  // way to get 100k objects into a browser at all, so the remaining half of
  // that row could not be measured by anyone, with any machine.
  //
  //     https://morph.carminio.org/?scale=100000
  //
  // Behind a parameter and never persisted: it is a measuring instrument, not a
  // feature, and a hundred thousand synthetic rows written into somebody's
  // IndexedDB because they mistyped a URL would be a bug of its own. The
  // bootstrap below is written from `pools`, and this pool is appended after
  // that call for exactly that reason.
  // NOT on the measurement route.
  //
  // The bootstrap remembers manifest refs so the next session reuses its pools
  // instead of reseeding them. On this route those refs address a memory store
  // that dies with the tab — so writing them poisons the next NORMAL session:
  // `addresses()` cannot resolve them, every pool reseeds, and the real
  // IndexedDB quietly accumulates a duplicate set. Measured, not reasoned:
  // objects went 47 -> 94, keys 24 -> 48, manifests 4 -> 8 on the reload after
  // a scale run.
  //
  // I introduced this while fixing the persistence defect @Codex found, and
  // caught it only because that fix made me check IndexedDB record counts
  // rather than whether the pool pill had disappeared. The first check would
  // have passed.
  if (scale === 0) {
    // The return value is deliberately unused, and @Codex's P1 on #164 is why
    // this now says so.
    //
    // The finding: the document keys and manifests are already durable in
    // IndexedDB here, this row is the only thing that can address them again,
    // so a blocked or full localStorage strands them. True as far as it goes,
    // and the destructive half of it — *"previously authored block graphs
    // remain in IndexedDB but `loadDocument` can no longer address them"* —
    // cannot be reached. Keys are minted only when the bootstrap has no
    // document entry (above), and a block can only be authored under keys that
    // were recorded, so a bootstrap missing them is a bootstrap under which
    // nothing was ever written.
    //
    // Throwing here was tried and `notes-upgrade-rolls-back-when-bootstrap-
    // write-fails` refused it: that guard requires the app to still OPEN when
    // storage is blocked, on the vocabulary it already had, and to upgrade
    // cleanly on a later retry. Degrading beats failing to start, and the
    // upgrade path above already rolls back where there is a journal entry to
    // undo. What is left is an orphaned key set in IndexedDB on a run that
    // authored nothing — a leak, not a loss.
    writeBootstrap({
      document: document as unknown as Bootstrap['document'],
      pools: Object.fromEntries(pools.map((p) => [p.id, { manifest: p.manifest, keys: p.keys }])),
    });
  }

  // The store is in memory on this route (see above), so these rows exist for
  // the lifetime of the tab and no longer. The ordering here no longer carries
  // the safety argument — the store choice does.
  if (scale > 0) {
    const scalePool = await seedScalePool(store, editor, scale);
    // The measurement route owns a memory store on purpose. Exposing the
    // ordinary pools beside Scale made their normal edit controls look durable:
    // an edit repainted correctly and then vanished on reload. It also made the
    // frame harness render an unrelated shell collection while claiming to
    // measure one 100k-object lens. Keep the instrument honest and make the
    // ephemeral route incapable of accepting ordinary user data.
    return {
      store, editor, pools: [scalePool], seeded: true,
      backend: store.backend, blobs, ...(direct ? { direct } : {}), document,
      ...effective(store, document, [scalePool]),
    };
  }

  /**
   * THE VAULT ARRIVES AS A VAULT — task `0199` half 2, Obsidian finding 6.
   *
   * Three seeded notes get canonical document content, written through
   * `saveDocument` rather than into the seed's edit session: that call does
   * its own read and its own edit, and a document is a graph of objects rather
   * than a property on one.
   *
   * ON A NOTE THAT IS STILL THE SEEDED ONE, per note, which is not the same
   * thing as on a fresh seed and is what the block used to say. The bar's
   * sentence is about what a person MEETS when they open the vault; a
   * returning store keeps the notes it has, and a person who has been typing
   * in them would not thank us for a seed rewriting their document under
   * them. Both are answered by the two terms below rather than by a fact
   * about the run — see the P2 note under this one.
   *
   * A refusal is not fatal: the note falls back to the migrated paragraph it
   * had, and the next boot finds the same gap and tries again.
   */
  /* NOT GATED ON THE RUN AT ALL — @Codex, P2 on `d80adab`, and it is the
     mirror of the P1 above it rather than a reversal.

     The gate was `freshPools.has('notes')`: a process-local set, so a first
     boot interrupted AFTER `writeBootstrap` recorded the notes pool and
     BEFORE these saves finished left the pool recorded and its documents
     absent — and every later boot then saw a pool that was not fresh and
     skipped them permanently. A partial seed that cannot heal. The same
     permanent gap opened whenever `saveDocument` returned an unsuccessful
     `CommitResult`, because the result was thrown away.

     The P1 that put the gate there was right about the DANGER and wrong
     about the QUESTION. Its danger is overwriting a note a person has
     rewritten; the question that answers it is not *was this pool created on
     this run* but *is this note still exactly as it was seeded*. Two terms
     answer it per note, and both must hold:

       already === undefined   the note has no canonical document, so there is
                               nothing of anybody's to replace
       held === body           and its body slot is still the seed's own text,
                               so nobody has rewritten it through the slot

     A person who wrote in the canvas has a document and fails the first. A
     person who rewrote the body without one fails the second. An interrupted
     first boot passes both, which is the case that could not heal before.

     The read-back of the note's body is the second term now rather than a
     defence in depth whose control could never be run: `notes-vault-upgrade`
     shows the block declining a note whose body has moved on, which is a
     clause that goes red rather than a paragraph asking to be believed. */
  {
    const notesPool = pools.find((p) => p.id === 'notes');
    if (notesPool) {
      const ids = await seedNoteIds(store, notesPool);
      for (const [title, body] of NOTE_ROWS.map(([t, b]) => [t, b] as const)) {
        const structure = NOTE_DOCUMENTS[title];
        const object = ids.get(title);
        if (!structure || !object) continue;
        const prefix = `document-${String(object)}-seed`;
        /* THE CHEAPEST QUESTION FIRST, and it is the one that answers for
           every note on every boot after the first. Reading the body and
           building the block list before asking whether a document already
           exists was work thrown away eight times per open — the boot cost the
           old run-scoped gate was partly there to avoid, and the reason to
           order these rather than to keep the gate. */
        /* A READ FAILURE IS NOT AN ABSENT DOCUMENT — @Codex's P2 on
           `ee1dd16`. `.catch(() => undefined)` folded a malformed canonical
           graph (a linked block with no kind, say) into the same answer as a
           note that never had one; with the legacy scalar already retired the
           body guard below then passes too, and the seed OVERWRITES the
           damaged graph instead of leaving it for recovery. Catching keeps
           boot alive, which is right; what it may not do is decide.

           NO CLAUSE, AND THE REASON IS THE FINDING. A control was built and
           could not be made to go red: a canonical graph malformed enough for
           `loadDocument` to reject is malformed enough for `saveDocument`'s
           own read to reject too, so with the catch restored the seed still
           writes nothing and both worlds observe identically. And the exact
           case @Codex named — a malformed graph whose legacy scalar was
           already retired — is closed by the body guard below it instead:
           a retired scalar leaves `held` undefined, which no longer qualifies
           as a missing seed. This is kept because deciding on an error is
           wrong however unreachable the loss is today, and `0034` says a
           clause that cannot fail is worse than none. */
        /* BOTH PREDICATES AND THE WRITE SHARE ONE BASIS — task `0219`, from
           @Codex's P1. These were three reads on three snapshots: this one,
           the body guard below, and `saveDocument`'s own. Two tabs opening an
           older database was the case they named — one observes that this note
           has no canonical document, the other authors one, and the first then
           writes anyway, replacing somebody's roots and text with the stock
           seed.

           One `read` now answers both questions, and its `revision` is handed
           to the write as its commit basis. Anything landing in between makes
           that basis stale and the commit is REFUSED, which is the right
           outcome rather than a failure: the note has a document now, so not
           seeding it is correct, and the `written.ok` check below already
           continues past exactly that. */
        let checked;
        try {
          checked = await store.read(async ({ reader, revision }) => ({
            already: await loadDocument(reader, object, document.keys, document.manifests),
            stored: await reader.get(object, { manifest: notesPool.manifest }),
            basis: revision,
          }));
        } catch { continue; }
        if (checked.already) continue;
        /* THE LEAD IS THE NOTE'S BODY AS IT STANDS, read back rather than
           taken from the table — the second of the two guards @Codex's P1 asks
           for, and the one that holds even if the first is ever wrong. The seed
           table's text is the fallback for the ordinary case where they are
           the same string; where they are not, the person wrote the difference.
           So an excerpt, a search hit and a card's first line all still say
           what they said, whoever wrote them. */
        const held = (checked.stored?.roles.get('body' as Role) ?? [])
          .map((v) => ('v' in v ? String(v.v) : ''))
          .find((t) => t.trim() !== '');
        /* THE NOTE MUST STILL BE THE ONE THAT WAS SEEDED, and EMPTY IS NOT
           THAT — @Codex's P2 on `ee1dd16`, correcting a guard shipped an hour
           earlier. It read `held !== undefined && held !== body`, so a note
           whose body somebody had deliberately CLEARED fell through the
           `undefined` arm and received the stock heading, list and quote on
           the next boot. A cleared body is a decision, not a gap.

           Only the unchanged seed text qualifies now, which is the whole
           guard: this note has no document, and its body is still the string
           the seed wrote. Anything else — rewritten, emptied, or absent
           because the note is not one of ours — is somebody's, and is left. */
        if (held !== body) continue;
        const authored = [{ kind: 'paragraph' as const, text: body }, ...structure];
        /**
         * A RUN OF ITEMS IS A LIST, and the first version of this seeded bare
         * `list-item` roots. Measured: the canvas drew `P H2 P LI LI LI
         * BLOCKQUOTE` — three `<li>` with no `<ul>` around them, which is
         * invalid markup and an unnumbered nothing to a screen reader.
         *
         * The vocabulary already says so: `list` is a container with
         * `children` and `list-item` is what goes in it, and the editor's own
         * typing path mints exactly that pair. A seed that writes the same
         * content by hand has to write the same shape — the canvas is not the
         * place to teach a second one.
         */
        const blocks: { id: string; kind: DocumentBlockKind; text?: string;
          level?: 2; children?: string[] }[] = [];
        const roots: string[] = [];
        let open: { id: string; children: string[] } | undefined;
        authored.forEach((block, i) => {
          const id = `${prefix}-${i}`;
          if (block.kind === 'list-item') {
            if (!open) {
              open = { id: `${prefix}-list-${i}`, children: [] };
              blocks.push({ id: open.id, kind: 'list', children: open.children });
              roots.push(open.id);
            }
            open.children.push(id);
            blocks.push({ id, kind: 'list-item', text: block.text });
            return;
          }
          open = undefined;
          blocks.push({ id, kind: block.kind, text: block.text,
            ...('level' in block && block.level !== undefined ? { level: block.level } : {}) });
          roots.push(id);
        });
        try {
          /* RETIRING THE LEGACY SCALAR, as the product's own migration does
             (`main.ts`'s `saveCanvasDocument(document, content, retirement)`).
             Without it the note carries two bodies: the seed's one-paragraph
             string in its `body` slot and the document beside it, with the
             derived boundary answering from the document and the slot sitting
             there as a stale duplicate nobody reads. `coda-row`'s formula
             clause found exactly that — a cell saying 83 words beside a probe
             reading the 17-word slot — and two truths is what it was reading. */
          /* THE RESULT IS READ. A refused commit used to be discarded, which
             under the old run-scoped gate meant the document was never
             written and never retried. It is not fatal — the note keeps the
             paragraph its body migrates to — but it must not look like a
             success either, and the next boot now finds the same note with no
             document and its body untouched, so it tries again. */
          /* VERSION 1 ON PURPOSE, and it must stay — task `0200`, decision
             `0075`. These seeded documents carry QUOTE blocks, which version 1
             does not admit, so they are precisely the grandfathered case
             @Codex's P1 named: read because a v1 envelope carrying a v2 kind is
             not malformed, and upgraded to 2 by the first genuine edit, because
             the writer derives the stamped version from the content. Changing
             this literal to the current version would quietly retire the only
             real-data instance of that path. */
          const written = await saveDocument(store, editor, object,
            { version: 1, roots, blocks },
            document.keys, document.manifests,
            notesPool.keys['body'] ? { scalar: notesPool.keys['body'] } : undefined,
            checked.basis);
          if (!written.ok) continue;
        } catch { /* the note keeps the paragraph its body migrates to */ }
      }
    }
  }

  // Listen only after bootstrap has settled and the complete DemoStore can be
  // returned. A notice during seeding must not call into a half-built host.
  const tabs = channel && store instanceof StoreEngine
    ? attachTabInvalidation({ engine: store, store: DB_NAME, channel,
      ...(options.onRefreshed ? { onRefreshed: options.onRefreshed } : {}) })
    : undefined;
  return { store, editor, pools, seeded, backend: store.backend, blobs,
    ...(direct ? { direct } : {}), document,
    ...effective(store, document, pools),
    ...(tabs ? { tabs } : {}) };
}

/**
 * Whether a remembered manifest still addresses objects in THIS store.
 *
 * Both calls throw for a ref the store has never heard of — `register` and
 * `count` go through `StoreEngine.manifestOf`, which throws rather than
 * returning undefined — and neither was guarded, so `openDemoStore` rejected,
 * `void boot()` had nothing to catch it, and the page rendered nothing at all.
 * That is the very state the trust check exists for: a bootstrap in
 * localStorage outliving the objects it points at, either because IndexedDB was
 * unavailable and the previous session ran in memory, or because the database
 * was cleared and localStorage was not.
 *
 * A pool at a time, so one unresolvable ref reseeds one pool rather than
 * blanking the page.
 */
async function addresses(
  store: ObjectStore, manifest: ManifestRef, typeHint: string,
): Promise<boolean> {
  const scope: Scope = { manifest, typeHint };
  try {
    // Registering keeps live counters for the scope; it is also the first call
    // that can throw, which is why it sits inside the same guard as the count.
    if (store instanceof StoreEngine) store.register(scope);
    return (await store.count(scope)) > 0;
  } catch {
    return false;   // unknown to this store — reseeding is the honest answer
  }
}

/**
 * A pool seeded by an older version of this file, upgraded structurally.
 *
 * Decision 0018 draws the boundary: migration may extend the manifest so a
 * returning vault understands newly declared properties, but it must never
 * populate those properties from today's seed tables. Seeds initialize an
 * empty vault; they are not desired state for data a person may have edited or
 * deliberately unlinked.
 *
 * Keep this path structural. A future data conversion needs an explicit,
 * versioned migration with its own identity and conflict policy.
 */
/**
 * The scope a pool is read through, including the documents it declared.
 *
 * `across` is spelled in POOL_DEFS as pool IDS and resolved to manifest refs
 * here, because a manifest ref is content-addressed: it does not exist until
 * the pool has been seeded, so nothing static can name one. A declared pool
 * that is not (yet) built is dropped rather than thrown on — the loop seeds in
 * order, and a reference that does not resolve is a better failure than a
 * bootstrap that cannot complete.
 */
function scopeFor(
  def: { readonly typeHint: string; readonly across?: readonly string[] },
  manifest: ManifestRef, done: readonly DemoPool[],
): Scope {
  const refs = (def.across ?? [])
    .map((id) => done.find((p) => p.id === id)?.manifest)
    .filter((m): m is ManifestRef => m !== undefined);
  return { manifest, typeHint: def.typeHint, ...(refs.length ? { across: refs } : {}) };
}

/**
 * Seed notes by their canonical title, for anything that needs to point at one
 * from outside the notes pool.
 *
 * Titles are mutable and non-unique, so they are not migration identities.
 * Match the stable seed tuple without its title or body instead: seed bodies
 * have changed between shipped demo versions, while author/date/quantity have
 * remained stable. This finds a renamed canonical note, refuses a user note
 * that merely reuses the old title, and marks a duplicate tuple `null` — which
 * every caller reads as "do not link this one" — rather than choosing whichever
 * object happened to be queried last.
 *
 * Lifted out of `upgradePool`, which had it inline, when the cross-doc
 * reference needed exactly the same question answered from the tasks pool.
 */
async function seedNoteIds(
  store: ObjectStore, notes: DemoPool | undefined,
): Promise<Map<string, ObjectId | null>> {
  const found = new Map<string, ObjectId | null>();
  if (!notes) return found;
  const byFingerprint = new Map(NOTE_ROWS.map(
    ([title, , author, written, words]) => [
      JSON.stringify([['text', author], ['date', written], ['number', words]]),
      title,
    ] as const,
  ));
  const page = await store.query(notes.scope, { limit: 500 });
  for (const row of page.items) {
    const object = await store.get(row.id, { manifest: notes.manifest });
    const fingerprint = JSON.stringify(
      (['author', 'date', 'quantity'] as const).map((role) => {
        const value = object?.roles.get(role)?.[0];
        return value && 'v' in value ? [value.k, value.v] : null;
      }),
    );
    const canonicalTitle = byFingerprint.get(fingerprint);
    if (!canonicalTitle) continue;
    found.set(canonicalTitle, found.has(canonicalTitle) ? null : row.id);
  }
  return found;
}

interface PoolUpgrade {
  readonly pool: DemoPool;
  /** Present only when this call changed durable store state. */
  readonly journal?: JournalId;
}

async function upgradePool(
  store: ObjectStore, editor: EditorAuthority, pool: DemoPool,
  done: readonly DemoPool[],
): Promise<PoolUpgrade> {
  const missing = pool.defs.filter((d) => !pool.keys[d.handle]);
  // Even with nothing to define, the scope may have gained a declared
  // document: `across` lives in POOL_DEFS, not in the saved bootstrap, so a
  // returning session builds it here from the pools already in hand.
  if (missing.length === 0) {
    const scope = scopeFor(pool, pool.manifest, done);
    if (store instanceof StoreEngine) store.register(scope);
    return { pool: { ...pool, scope } };
  }

  // Define every missing property, but do not write values into existing
  // objects. A new schema entry is not consent to apply today's seed data.
  const added = {} as Record<string, PropertyKeyId>;
  const committed = await store.edit(editor, (await store.snapshot()).revision, (s) => {
    for (const d of missing) {
      added[d.handle] = s.defineProperty({ label: d.label, kind: d.kind, roleHint: d.role });
    }
  });
  if (!committed.ok) throw new Error(`upgrading ${pool.id} refused: ${committed.code}`);

  let manifest: ManifestRef;
  try {
    manifest = await store.extendManifest(editor, pool.manifest,
      missing.map((d) => ({ role: d.role, key: added[d.handle]! })));
  } catch (error) {
    const rolledBack = await store.undo(editor,
      (await store.snapshot()).revision, committed.journal.id);
    if (!rolledBack.ok) {
      throw new Error(
        `upgrading ${pool.id} could not extend its manifest or roll back: `
        + rolledBack.code,
      );
    }
    throw error;
  }
  const scope = scopeFor(pool, manifest, done);
  if (store instanceof StoreEngine) store.register(scope);

  return {
    pool: { ...pool, manifest, scope, keys: { ...pool.keys, ...added } },
    journal: committed.journal.id,
  };
}

async function seedPool(
  store: ObjectStore, editor: EditorAuthority,
  def: Omit<DemoPool, 'manifest' | 'scope' | 'keys'>,
  done: readonly DemoPool[],
): Promise<{ pool: DemoPool; journal: JournalId }> {
  const keys = {} as Record<string, PropertyKeyId>;
  const byPerson = new Map<string, ObjectId>();

  // Read before writing: the edit callback is synchronous, and a cross-doc
  // reference needs ids from a pool this call did not create. Empty for every
  // pool that declares no document, which is every pool but one.
  const crossDoc = def.across?.includes('notes')
    ? await seedNoteIds(store, done.find((p) => p.id === 'notes'))
    : new Map<string, ObjectId | null>();

  const committed = await store.edit(editor, (await store.snapshot()).revision, (s) => {
    for (const d of def.defs) {
      keys[d.handle] = s.defineProperty({ label: d.label, kind: d.kind, roleHint: d.role });
    }

    if (def.id === 'films') {
      for (const [, , director] of FILM_ROWS) {
        if (byPerson.has(director)) continue;
        const bio = DIRECTOR_ROWS[director];
        const properties: (readonly [PropertyKeyId, Value])[] = [[keys.title!, text(director)]];
        if (bio) {
          properties.push([keys.date!, { k: 'date', v: bio[0] }]);
          properties.push([keys.origin!, { k: 'enum', v: bio[1] }]);
        }
        byPerson.set(director, s.create({ typeHint: 'person', properties }));
      }
      for (const [title, released, director, runtime, score, origin, poster] of FILM_ROWS) {
        const properties: (readonly [PropertyKeyId, Value])[] = [
          [keys.title!, text(title)],
          [keys.date!, { k: 'date', v: released }],
          [keys.author!, text(director)],
          [keys.duration!, { k: 'number', v: runtime }],
          [keys.poster!, { k: 'media', v: { blob: poster, mime: 'image/jpeg' } }],
          [keys.person!, { k: 'ref', v: byPerson.get(director)! }],
        ];
        // Ragged on purpose: a missing property is ABSENT, never a placeholder,
        // so deriveShape sees a real hole and coverage falls below 1.
        if (score !== null) properties.push([keys.score!, { k: 'number', v: score }]);
        if (origin !== null) properties.push([keys.origin!, { k: 'enum', v: origin }]);
        s.create({ typeHint: def.typeHint, properties });
      }
    }

    if (def.id === 'archive') {
      for (const row of ARCHIVE_ROWS) {
        const properties: (readonly [PropertyKeyId, Value])[] = [
          [keys.title!, text(row.title)],
          [keys.date!, { k: 'date', v: row.date }],
          [keys.author!, text(row.author)],
          [keys.transcript!, text(row.transcript)],
          // The pool's one convention, and the only unanimous role in the demo.
          // Task 0092: `deriveTemplate` calls a role unanimous at coverage 1 and
          // one distinct value, and until this existed the rule fired on nothing
          // — an affordance with nothing behind it, so the capability was not
          // claimed. Every row carries it because every row genuinely came from
          // there; the media URLs above say `archive.org` without exception.
          [keys.source!, { k: 'enum', v: row.source }],
        ];
        // Absent, never a placeholder — the same rule the films pool follows, and
        // the reason `deriveShape` sees a real hole instead of a full column.
        const video = (row as { video?: string }).video;
        const audio = (row as { audio?: string }).audio;
        const poster = (row as { poster?: string }).poster;
        if (video) properties.push([keys.video!, { k: 'media', v: { blob: video, mime: 'video/mp4' } }]);
        if (audio) properties.push([keys.audio!, { k: 'media', v: { blob: audio, mime: 'audio/mpeg' } }]);
        if (poster) properties.push([keys.poster!, { k: 'media', v: { blob: poster, mime: 'image/jpeg' } }]);
        s.create({ typeHint: def.typeHint, properties });
      }
    }

    if (def.id === 'notes') {
      // Two passes, because a citation graph has cycles and the first pass
      // cannot reference an object the second pass has not created yet.
      // Creation order is left exactly as NOTE_ROWS has it, so the ids stay in
      // the order the table already displays.
      const byNote = new Map<string, ObjectId>();
      for (const [title, body, author, written, words] of NOTE_ROWS) {
        byNote.set(title, s.create({ typeHint: def.typeHint, properties: [
          [keys.title!, text(title)],
          [keys.body!, text(body)],
          [keys.author!, text(author)],
          [keys.date!, { k: 'date', v: written }],
          [keys.quantity!, { k: 'number', v: words }],
        ] }));
      }
      for (const [from, targets] of Object.entries(NOTE_LINKS)) {
        const source = byNote.get(from);
        // A citation naming a note that does not exist is a typo in the seed,
        // not something to paper over: it would otherwise seed a vault quietly
        // missing the links it claims to have.
        if (!source) throw new Error(`NOTE_LINKS names an unknown note: ${from}`);
        targets.forEach((title, i) => {
          const to = byNote.get(title);
          if (!to) throw new Error(`${from} cites an unknown note: ${title}`);
          // The first citation fills the ref slot, which is what puts
          // `relation.peer` in the derived shape. The rest become edges, which
          // traverse follows just the same — so the graph draws every link
          // while the shape still has a role for its edge channel to bind.
          if (i === 0) s.set(source, keys.link!, { k: 'ref', v: to });
          else s.link(source, keys.link!, to);
        });
      }
    }

    if (def.id === 'features') {
      // People first, exactly the films/directors pattern: typed entities in
      // this manifest, so the assignee picker offers people rather than the
      // features it sits among.
      for (const row of FEATURE_ROWS) {
        const person = row[4];
        if (!person || byPerson.has(person)) continue;
        byPerson.set(person, s.create({ typeHint: 'person', properties: [
          [keys.title!, text(person)],
        ] }));
      }
      for (const [title, stage, effort, due, assignee] of FEATURE_ROWS) {
        const properties: (readonly [PropertyKeyId, Value])[] = [
          [keys.title!, text(title)],
          [keys.status!, { k: 'enum', v: stage }],
          [keys.effort!, { k: 'number', v: effort }],
        ];
        // Absent, never a placeholder — the raggedness rule every pool here
        // follows. The sprint ref is absent for EVERY feature at this point:
        // sprints do not exist yet, and the sprints branch below writes both
        // directions of each link in one edit.
        if (due) properties.push([keys.due!, { k: 'date', v: due }]);
        if (assignee) properties.push([keys.assignee!, { k: 'ref', v: byPerson.get(assignee)! }]);
        featureIdsByTitle.set(title, s.create({ typeHint: def.typeHint, properties }));
      }
    }

    if (def.id === 'sprints') {
      // The features pool's keys, for writing the OTHER side of each link.
      // `done` holds every pool seeded before this one, and features precedes
      // sprints in POOL_DEFS for exactly this reason.
      const features = done.find((p) => p.id === 'features');
      for (const [title, goal, lead, start, end] of SPRINT_ROWS) {
        const sprint = s.create({ typeHint: def.typeHint, properties: [
          [keys.title!, text(title)],
          [keys.goal!, text(goal)],
          [keys.author!, text(lead)],
          [keys['start']!, { k: 'date', v: start }],
          [keys['end']!, { k: 'date', v: end }],
        ] });
        // An EMPTY map is the recovery state, not a typo: features took the
        // saved path this open (recorded, seeded on a prior load whose
        // sprints write was lost), so their ids were never captured here.
        // Seed the sprints with their feature slots ABSENT — the documented
        // degradation — rather than guessing ids from edited data. The
        // typo-throw below stays for a POPULATED map missing one title,
        // which can only be a seed-table mistake. Seen red first: without
        // this guard the recovery test dies on the throw, every load,
        // exactly as @Codex's P1 predicted.
        if (!features || featureIdsByTitle.size === 0) continue;
        // BOTH directions of every link, in this one edit: the feature's
        // `Sprint` slot points up, and the sprint's `Features` holds the
        // first feature in its slot with the rest as edges — the notes
        // citation storage split, applied across a document boundary.
        const carried = FEATURE_ROWS.filter((r) => r[5] === title);
        carried.forEach((r, i) => {
          const feature = featureIdsByTitle.get(r[0]);
          if (!feature) throw new Error(`sprint ${title} names an unknown feature: ${r[0]}`);
          s.set(feature, features.keys['sprint']!, { k: 'ref', v: sprint });
          if (i === 0) s.set(sprint, keys['feature']!, { k: 'ref', v: feature });
          else s.link(sprint, keys['feature']!, feature);
        });
      }
    }

    if (def.id === 'tasks') {
      for (const [title, status, area, owner, updated, started, finished] of TASK_ROWS) {
        // THE CROSS-DOC REFERENCE, written as an ordinary ref. Absent when
        // this task is about no note, which is more than half of them — the
        // same raggedness rule every other pool here follows.
        const note = TASK_NOTES[title] ? crossDoc.get(TASK_NOTES[title]!) : undefined;
        s.create({ typeHint: def.typeHint, properties: [
          [keys.title!, text(title)],
          [keys.status!, { k: 'enum', v: status }],
          [keys['area']!, { k: 'enum', v: area }],
          [keys.author!, text(owner)],
          [keys.date!, { k: 'date', v: updated }],
          [keys['start']!, { k: 'date', v: started }],
          // An unfinished task has no end. Written as ABSENT rather than as an
          // empty string, so the store holds "no value" instead of a value
          // that fails to parse — a distinction the timeline reads to tell a
          // moment from a bar.
          ...(finished ? [[keys['end']!, { k: 'date' as const, v: finished }]] as const : []),
          ...(note ? [[keys['note']!, { k: 'ref' as const, v: note }]] as const : []),
        ] });
      }
    }
  });
  if (!committed.ok) throw new Error(`seeding ${def.id} refused: ${committed.code}`);

  const manifest = await store.putManifest(editor,
    def.defs.map((d) => ({ role: d.role, key: keys[d.handle]! })));
  const scope = scopeFor(def, manifest, done);
  if (store instanceof StoreEngine) store.register(scope);

  // The journal rides along so the caller can UNDO this seed if it cannot be
  // recorded — the upgrade path's discipline, owed here since the per-pool
  // bootstrap write landed (@Codex, P1 on e673be9: a readable-but-unwritable
  // localStorage — quota exceeded, the state notes-vault-upgrade explicitly
  // models — kept the old bootstrap forever while every reload seeded another
  // durable copy of the new pools).
  return { pool: { ...def, manifest, scope, keys }, journal: committed.journal.id };
}
